// Imports
import Link from "next/link";
import { Img } from "../elements";
import { SimpleContent } from "@/components";
import { ContentButtonLink } from "../elements/ContentButtonLink";
import {
  Accordion as ShadAccordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { imageContainerStyles, resolveLink } from "@/lib/utils";

// Content
export function Content({ content, className, centerButtons = false }) {
  return (
    <div className="flex flex-col space-y-5">
      {content?.map((item, index) => {
        switch (item.type) {
          case "text":
            return (
              <div
                key={index}
                className={`content-block ${className}`}
                dangerouslySetInnerHTML={{ __html: item?.text }}
              />
            );
          case "button":
            return (
              <div
                key={index}
                className={centerButtons ? "flex justify-center w-full" : undefined}
              >
                <ContentButtonLink item={item} />
              </div>
            );
          case "list_grid":
            return (
              <div
                className="grid grid-cols-2 gap-2 xl:grid-cols-3 lg:gap-5"
                key={index}
              >
                {item.grid_items.map((gridItem, index) => (
                  <div
                    className="flex justify-center items-center p-2 text-center text-card-foreground bg-white lg:p-5 rounded-card-radius"
                    key={index}
                  >
                    <p>{gridItem.item}</p>
                  </div>
                ))}
              </div>
            );
          case "expanded_list_grid":
            return (
              <div
                className="grid grid-cols-2 gap-2 mb-10 xl:grid-cols-3 lg:gap-5"
                key={index}
              >
                {item.grid_items.map((gridItem, subIndex) => {
                  const rawUrl = resolveLink(gridItem?.url);
                  const hasUrl =
                    rawUrl != null && String(rawUrl).trim().length > 0;
                  const isExternal =
                    typeof rawUrl === "string" && rawUrl.includes("http");
                  const openInNew = Boolean(gridItem?.open_in_new_window_tab);
                  const target =
                    openInNew || isExternal ? "_blank" : "_self";

                  const cellBase =
                    "flex justify-center items-center p-2 text-center lg:p-5 rounded-card-radius border shadow-lg border-foreground/20 bg-card text-card-foreground overflow-hidden transition-all duration-200";
                  const linkCell =
                    "group cursor-pointer hover:bg-card/80 hover:shadow-xl";
                  const linkText =
                    "block w-full transition-colors group-hover:text-button";

                  return (
                    <div
                      className={`${cellBase} ${hasUrl ? linkCell : ""}`}
                      key={subIndex}
                    >
                      {hasUrl ? (
                        isExternal ? (
                          <a
                            href={rawUrl}
                            target={target}
                            rel="noopener noreferrer"
                            className={linkText}
                          >
                            {gridItem.item}
                          </a>
                        ) : (
                          <Link href={rawUrl} target={target} className={linkText}>
                            {gridItem.item}
                          </Link>
                        )
                      ) : (
                        <p className="w-full">{gridItem.item}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          case "image":
            return (
              <div key={index}>
                {resolveLink(item.link_image) ? (
                  <Link href={resolveLink(item.link_image)} target="_blank">
                    <div
                      className={imageContainerStyles({
                        height: item.image.contain_image ? "none" : item.height,
                        width: item.width.value,
                      })}
                    >
                      <Img
                        data={item.image}
                        sizes="(max-width: 1024px) 100vw, 1600px"
                        fill
                      />
                    </div>
                  </Link>
                ) : (
                  <div
                    className={imageContainerStyles({
                      height: item.image.contain_image ? "none" : item.height,
                      width: item.width.value,
                    })}
                  >
                    <Img
                      data={item.image}
                      sizes="(max-width: 1024px) 100vw, 1600px"
                      fill
                    />
                  </div>
                )}
              </div>
            );

          case "accordion":
            return <Accordion key={index} items={item?.items} />;
          case "columns":
            return <Columns key={index} items={item?.columns} />;
          case "wrapped_image":
            return (
              <WrappedImage
                key={index}
                image={item?.image}
                imageLink={resolveLink(item?.link_image)}
                margin={item?.margin?.value}
                size={item?.size?.value}
                alignment={item?.alignment?.value}
                content={item?.content}
              />
            );
          default:
            return null;
        }
      })}
    </div>
  );
}

function Accordion({ items }) {
  return (
    <div className="flex flex-col space-y-5">
      <ShadAccordion
        type="single"
        collapsible
        className="text-left border-t border-black"
      >
        {items.map((item, index) => (
          <AccordionItem value={"item-" + index} key={index}>
            <AccordionTrigger className="text-left">
              {item?.heading}
            </AccordionTrigger>
            <AccordionContent className="">
              <SimpleContent content={item?.content} />
            </AccordionContent>
          </AccordionItem>
        ))}
      </ShadAccordion>
    </div>
  );
}

function Columns({ items }) {
  return (
    <div
      className="grid w-full gap-5 grid-cols-2 lg:grid-cols-[repeat(var(--cols),1fr)]"
      style={{
        "--cols": items.length,
      }}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className="w-full content-block"
          dangerouslySetInnerHTML={{ __html: item.content }}
        />
      ))}
    </div>
  );
}

export function WrappedImage({
  image,
  imageLink,
  margin,
  size,
  alignment,
  content,
}) {
  console.log("IMAGE", image, imageLink, margin, size, alignment);

  const getMarginClass = (margin, alignment) => {
    switch (margin) {
      case "none":
        return "";
      case "sm":
        if (alignment === "top-left") {
          return "lg:mb-0 mb-2 mr-2";
        }
        if (alignment === "top-right") {
          return "lg:mb-0 mb-2 ml-2";
        }
      case "md":
        if (alignment === "top-left") {
          return "lg:mb-0 mb-4 mr-4";
        }
        if (alignment === "top-right") {
          return "lg:mb-0 mb-4 ml-4";
        }
      case "lg":
        if (alignment === "top-left") {
          return "lg:mb-0 mb-2 mr-6";
        }
        if (alignment === "top-right") {
          return "lg:mb-0 mb-2 ml-6";
        }
      default:
        return "";
    }
  };

  const getSizeClass = (size) => {
    switch (size) {
      case "xs":
        return "lg:w-[200px] max-w-[100%]";
      case "sm":
        return "lg:w-[300px] max-w-[100%]";
      case "md":
        return "lg:w-[400px] max-w-[100%]";
      case "lg":
        return "lg:w-[500px] max-w-[100%]";
      case "xl":
        return "lg:w-[700px] max-w-[100%]";
      default:
        return "w-32";
    }
  };

  const getAlignmentClass = (alignment) => {
    switch (alignment) {
      case "top-left":
        return "float-left";
      case "top-right":
        return "float-right";
      default:
        return "float-left";
    }
  };

  const imageWrapper = (
    <span
      className={`${getMarginClass(margin, alignment)} ${getSizeClass(
        size
      )} ${getAlignmentClass(alignment)}`}
    >
      <Img data={image} sizes="(max-width: 1024px) 100vw, 1600px" />
    </span>
  );

  const wrappedImage = imageLink ? (
    <p>
      <Link href={imageLink} target="_blank">
        {imageWrapper}
      </Link>
    </p>
  ) : (
    <p className={""}>{imageWrapper}</p>
  );

  // // Split content into paragraphs and wrap each in a p tag
  const formattedContent = content
    ? [
        {
          type: "text",
          text: content,
        },
      ]
    : null;

  return (
    <div className="flex flex-col space-y-2 lg:space-y-0 lg:flex-row">
      {wrappedImage}
      <SimpleContent content={formattedContent} />
    </div>
  );
}
